import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import ContractHeader from "../../components/Contract/ContractHeader";
import ContractServiceTable from "../../components/Contract/ContractServiceTable";
import ContractServiceDetail from "../../components/Contract/ContractServiceDetail";
import ContractPaymentTable from "../../components/Contract/ContractPaymentTable";
import ContractActions from "../../components/Contract/ContractActions";
import ServiceModal from "../../components/Modal/Contract/ServiceModal";
import LocationModal from "../../components/Modal/LocationModal";
import api from "../../../utils/axios";
import Swal from "sweetalert2";
import { getReport } from "../../../utils/ReportUtils";
import { getSpRunNumber, getData } from "../../../utils/SamuiUtils";
import ContractServiceWorkTable from "../../components/Contract/ContractWorkTable";

export default function ContractPage({ mode = "edit" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [reload, setReload] = useState(false);
  const { AR_Id, Contract_Id, Doc_Id } = useParams();
  
  // ตรวจสอบว่าเป็น view mode หรือไม่ (จาก URL หรือ Status === 4)
  const isViewMode = location.pathname.includes("/view/") || mode === "view";

  const contractId =
    (Contract_Id && Contract_Id !== "create" ? Contract_Id : Doc_Id) || null;

  const comp = localStorage.getItem("company");

  const [header, setHeader] = useState({
    Comp_Id: comp ? Number(comp) : 1,
    Doc_No: "",
    Contract_Date: new Date().toISOString().split("T")[0],
    Start_Date: "",
    End_Date: "",
    Contract_Status: null,
    Doc_Remark: "",
    Doc_Detail: "",
    AR_Id: null,
    AR_Name: "",
    AR_ContactName: "",
    AR_Tel: "",
    AR_TaxNo: "",
    DisplayName: "",
    ServiceAddr_Id: null,
    ServiceAddr_Name: "",
    ServiceAddr_FullText: "",
    Service_ContactName: "",
    Service_ContactTel: "",
    Contract_ServiceAddr_Id: null,
    Contract_ServiceAddr_Name: "",
    Contract_ServiceAddr_FullText: "",
    Contract_ContactName: "",
    Contract_ContactTel: "",
    Sale_Emp_Id: null,
    Quote_No: "",
    Quote_Id: null,
    IsVat: 0,
    IsVatIn: 0,
    VatAmount: 0,
    Total_Price: 0,
    Discount: 0,
    NetPrice: 0,
    GrandTotal: 0,
    Service_Name: "",
    Language: "TH",
    Doc_Status: null,
    Service_Time: 1,
    Service_Unit: null,
    Contract_Period: null,
    Contract_Unit: null,
    Quote_SubLocations: [],
    Selected_SubLocation_Ids: [],
    Used_SubLocation_Ids: [], // จุดย่อยที่ถูกใช้ในสัญญาอื่นแล้ว (เลือกซ้ำไม่ได้)
  });

  const [services, setServices] = useState([]);
  const [serviceDetail, setServiceDetail] = useState({
    ExtraWorker: "",
    ServiceNote: "",
  });
  const [payments, setPayments] = useState([]);
  const [works, setWorks] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [isPaymentValid, setIsPaymentValid] = useState(true);

  const [serviceLocations, setServiceLocations] = useState([]);
  const [contractLocation, setContractLocation] = useState(null);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [contractLocationModalOpen, setContractLocationModalOpen] = useState(false);
  const [pendingRefreshServiceAddr, setPendingRefreshServiceAddr] = useState(null);
  const [pendingRefreshContractAddr, setPendingRefreshContractAddr] = useState(null);
  const [serviceTypeMaster, setServiceTypeMaster] = useState([]);

  const queryParams = new URLSearchParams(location.search);
  const quoteIdFromURL = queryParams.get("quote_id");
  const arIdFromURL = queryParams.get("ar_id");

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (contractId) {
          await loadContract();
          return;
        }

        if (!contractId && AR_Id) {
          setHeader((prev) => ({
            ...prev,
            AR_Id: Number(AR_Id),
            Doc_No: "",
          }));
          return;
        }
      } catch (err) {
        console.error("โหลดข้อมูลสัญญาผิดพลาด", err);
        Swal.close();
      }
    };

    fetchData();
  }, [contractId, quoteIdFromURL, AR_Id]);

  // โหลด master ประเภทสถานที่บริการสำหรับ Location Modal ตอนตรวจสอบ
  const loadServiceTypes = useCallback(async () => {
    try {
      const res = await getData(
        "Tb_Set_ServiceAddrType",
        "ServiceAddr_Type_Status = 'Y'"
      );
      const rows = Array.isArray(res) ? res : res?.data || [];
      const mapped = rows
        .map((item) => ({
          id: item?.ServiceAddr_Type_Id ?? item?.id ?? item?.Id,
          name: item?.ServiceAddr_Type_Name ?? item?.name ?? item?.ServiceAddr_Type,
        }))
        .filter((item) => item.id && item.name);
      setServiceTypeMaster(mapped);
    } catch (err) {
      console.error("โหลดประเภทสถานที่บริการไม่สำเร็จ:", err);
      setServiceTypeMaster([]);
    }
  }, []);

  useEffect(() => {
    loadServiceTypes();
  }, [loadServiceTypes]);

  // โหลดซ้ำเมื่อเปิด Location Modal เพื่อให้ได้ข้อมูลล่าสุด
  useEffect(() => {
    if (locationModalOpen) loadServiceTypes();
  }, [locationModalOpen, loadServiceTypes]);

  const loadServiceAddresses = useCallback(async (forceUpdate = false) => {
    const arId = header.AR_Id;
    if (!arId) return;

    try {
      const res = await api.get(`/get-tb-set-service-addr/${arId}`);
      if (res.data.status !== "OK") return;

      const list = res.data.result?.recordset || [];

      const isServiceAddr = (x) =>
        (x.ServicePurpose_Name || "").trim() === "ที่อยู่บริการ" ||
        Number(x.ServicePurpose) === 6;
      const isContractAddr = (x) =>
        (x.ServicePurpose_Name || "").trim() === "ที่อยู่ทำสัญญา" ||
        Number(x.ServicePurpose) === 7;

      const purpose3List = list.filter(isContractAddr);
      const purpose2List = list.filter(isServiceAddr);

      const contractLoc =
        purpose3List.find((x) => x.IsDefault === 1) ||
        purpose3List[0] ||
        null;

      const serviceLoc =
        purpose2List.find((x) => x.IsDefault === 1) ||
        purpose2List[0] ||
        null;

      setContractLocation(contractLoc);
      setServiceLocations(purpose2List);

      // Update header only if not already set (or forceUpdate)
      setHeader((prev) => {
        const updates = {};

        if (
          contractLoc &&
          (forceUpdate || !prev.Contract_ServiceAddr_Id ||
            !prev.Contract_ServiceAddr_FullText)
        ) {
          const full3 = [
            contractLoc.AddrNo || "",
            contractLoc.SubDistrictName
              ? `ต.${contractLoc.SubDistrictName}`
              : "",
            contractLoc.DistrictName ? `อ.${contractLoc.DistrictName}` : "",
            contractLoc.ProvinceName ? `จ.${contractLoc.ProvinceName}` : "",
            contractLoc.Zipcode || "",
          ]
            .filter(Boolean)
            .join(" ");

          updates.Contract_ServiceAddr_Id = contractLoc.ServiceAddr_Id;
          updates.Contract_ServiceAddr_Name = contractLoc.ServiceAddr_Name;
          updates.Contract_ServiceAddr_FullText = full3;
          updates.Contract_ContactName = contractLoc.AR_ContactName || "";
          updates.Contract_ContactTel = (contractLoc.AR_Tel || "").replace(
            /(\d{3})(\d{3})(\d{4})/,
            "$1-$2-$3"
          );
        }

        if (
          serviceLoc &&
          (forceUpdate || !prev.ServiceAddr_Id || !prev.ServiceAddr_FullText)
        ) {
          const full2 = [
            serviceLoc.AddrNo || "",
            serviceLoc.SubDistrictName
              ? `ต.${serviceLoc.SubDistrictName}`
              : "",
            serviceLoc.DistrictName ? `อ.${serviceLoc.DistrictName}` : "",
            serviceLoc.ProvinceName ? `จ.${serviceLoc.ProvinceName}` : "",
            serviceLoc.Zipcode || "",
          ]
            .filter(Boolean)
            .join(" ");

          updates.ServiceAddr_Id = serviceLoc.ServiceAddr_Id;
          updates.ServiceAddr_Name = serviceLoc.ServiceAddr_Name;
          updates.ServiceAddr_FullText = full2;
          updates.Service_ContactName = serviceLoc.AR_ContactName || "";
          updates.Service_ContactTel = (serviceLoc.AR_Tel || "").replace(
            /(\d{3})(\d{3})(\d{4})/,
            "$1-$2-$3"
          );
        }

        if (
          !contractLoc &&
          serviceLoc &&
          (forceUpdate || !prev.Contract_ServiceAddr_Id ||
            !prev.Contract_ServiceAddr_FullText)
        ) {
          const fallbackFull = [
            serviceLoc.AddrNo || "",
            serviceLoc.SubDistrictName
              ? `ต.${serviceLoc.SubDistrictName}`
              : "",
            serviceLoc.DistrictName ? `อ.${serviceLoc.DistrictName}` : "",
            serviceLoc.ProvinceName ? `จ.${serviceLoc.ProvinceName}` : "",
            serviceLoc.Zipcode || "",
          ]
            .filter(Boolean)
            .join(" ");

          updates.Contract_ServiceAddr_Id = serviceLoc.ServiceAddr_Id;
          updates.Contract_ServiceAddr_Name = serviceLoc.ServiceAddr_Name;
          updates.Contract_ServiceAddr_FullText = fallbackFull;
          updates.Contract_ContactName = serviceLoc.AR_ContactName || "";
          updates.Contract_ContactTel = (serviceLoc.AR_Tel || "").replace(
            /(\d{3})(\d{3})(\d{4})/,
            "$1-$2-$3"
          );
        }

        return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
      });
    } catch (err) {
      console.error("load service address fail:", err);
    }
  }, [header.AR_Id]);

  useEffect(() => {
    loadServiceAddresses();
  }, [loadServiceAddresses]);

  // Refresh service addresses when reload flag changes
  useEffect(() => {
    if (reload) {
      loadServiceAddresses();
      setReload(false);
    }
  }, [reload, loadServiceAddresses]);

  // หลัง locationModal ปิด — fetch address ใหม่และ update เฉพาะ header fields ที่เกี่ยวข้อง
  useEffect(() => {
    if (locationModalOpen || !pendingRefreshServiceAddr) return;
    const id = pendingRefreshServiceAddr;
    setPendingRefreshServiceAddr(null);
    api.get(`/get-tb-set-service-addr-by-id/${id}`).then((fresh) => {
      const rec = fresh.data.result?.recordset?.[0];
      if (!rec) return;
      const fullText = [
        rec.AddrNo,
        rec.SubDistrictName ? `ต.${rec.SubDistrictName}` : "",
        rec.DistrictName ? `อ.${rec.DistrictName}` : "",
        rec.ProvinceName ? `จ.${rec.ProvinceName}` : "",
        rec.Zipcode,
      ].filter(Boolean).join(" ");
      setHeader((prev) => ({
        ...prev,
        ServiceAddr_Name: rec.ServiceAddr_Name || prev.ServiceAddr_Name,
        ServiceAddr_FullText: fullText || prev.ServiceAddr_FullText,
        Service_ContactName: rec.AR_ContactName || prev.Service_ContactName,
        Service_ContactTel: rec.AR_Tel || prev.Service_ContactTel,
      }));
    });
  }, [locationModalOpen, pendingRefreshServiceAddr]);

  useEffect(() => {
    if (contractLocationModalOpen || !pendingRefreshContractAddr) return;
    const id = pendingRefreshContractAddr;
    setPendingRefreshContractAddr(null);
    api.get(`/get-tb-set-service-addr-by-id/${id}`).then((fresh) => {
      const rec = fresh.data.result?.recordset?.[0];
      if (!rec) return;
      const fullText = [
        rec.AddrNo,
        rec.SubDistrictName ? `ต.${rec.SubDistrictName}` : "",
        rec.DistrictName ? `อ.${rec.DistrictName}` : "",
        rec.ProvinceName ? `จ.${rec.ProvinceName}` : "",
        rec.Zipcode,
      ].filter(Boolean).join(" ");
      setHeader((prev) => ({
        ...prev,
        Contract_ServiceAddr_Name: rec.ServiceAddr_Name || prev.Contract_ServiceAddr_Name,
        Contract_ServiceAddr_FullText: fullText || prev.Contract_ServiceAddr_FullText,
        Contract_ContactName: rec.AR_ContactName || prev.Contract_ContactName,
        Contract_ContactTel: rec.AR_Tel || prev.Contract_ContactTel,
      }));
    });
  }, [contractLocationModalOpen, pendingRefreshContractAddr]);

  const loadContract = async () => {
    const res = await api.get(`/get-contract/${contractId}`);
    if (res.data.status !== "OK") throw new Error(res.data.message);

    const h = res.data.header || {};
    const d = res.data.details || [];

    const discount = Number(h.Discount_Value || 0);
    // เมื่อ IsVatIn ใช้ h.Total_Price จาก DB (เก็บ base) ไม่งั้นคำนวณจาก details
    const totalFromDetails = d.reduce((s, i) => s + (i.Item_Total || 0), 0);
    const totalPrice = h.Total_Price != null ? Number(h.Total_Price) : totalFromDetails;
    const net = totalPrice - discount;
    let vat = 0;
    let grand = net;
    if (h.IsVatIn === 1) {
      grand = net * 1.07; // Total_Price = base
      vat = grand - net;
    } else if (h.IsVat === 1) {
      vat = net * 0.07;
      grand = net + vat;
    }

    setHeader((prev) => ({
      ...prev,
      Doc_Id: h.Doc_Id || contractId,
      Doc_No: h.Doc_No,
      Contract_Date: h.Doc_Date?.split("T")[0] || "",
      Start_Date: h.Ct_Start_Date?.split("T")[0] || "",
      End_Date: h.Ct_End_Date?.split("T")[0] || "",
      AR_Id: h.AR_ID,
      AR_Name: h.AR_Name,
      AR_ContactName: h.AR_ContactName || "",
      DisplayName: h.DisplayName || "",
      AR_Tel: h.AR_Tel || "",
      AR_TaxNo: h.AR_TaxNo || "",
      ServiceAddr_Id: h.ServiceAddr_Id,
      ServiceAddr_Name: h.ServiceAddr_Name,
      ServiceAddr_FullText: h.ServiceAddr_FullText || "",
      Service_ContactName: h.Service_ContactName || "",
      Service_ContactTel: h.Service_ContactTel || "",
      Contract_ServiceAddr_Id: h.Contract_ServiceAddr_Id || null,
      Contract_ServiceAddr_Name: h.Contract_ServiceAddr_Name || "",
      Contract_ServiceAddr_FullText: h.Contract_ServiceAddr_FullText || "",
      Contract_ContactName: h.Contract_ContactName || "",
      Contract_ContactTel: h.Contract_ContactTel || "",
      Quote_No: h.Ref_DocNo,
      Quote_Id: h.Ref_DocID,
      Quote_SubLocations: res.data.quoteSubLocations || [],
      Selected_SubLocation_Ids: res.data.selectedSubLocationIds || [],
      Doc_Detail: h.Quote_Detail || "",
      Doc_Remark: h.Quote_Remark || "",
      IsVat: h.IsVat,
      IsVatIn: h.IsVatIn ?? 0,
      Total_Price: totalPrice,
      Discount: discount,
      NetPrice: h.IsVatIn === 1 ? grand : net,
      VatAmount: vat,
      GrandTotal: grand,
      Language: h.Contract_Lang || "TH",
      Doc_Status: h.Doc_Status,
      Sale_Emp_Id: h.Sale_Emp_Id || null,
      Service_Name: h.Service_Name || "",
      Service_Time: h.Service_Time || 1,
      Service_Unit: h.Service_Unit ?? null,
      Contract_Period: h.Contract_Period || null,
      Contract_Unit: h.Contract_Unit || null,
    }));

    // ต้องเรียก setServices ก่อน await ถัดไป เพื่อให้อยู่ใน React batch เดียวกับ setHeader
    // (ป้องกัน ContractWorkTable mount ด้วย contractServices=[] แล้ว fallback ไปดึง services ทั้งหมด)
    setServices(
      d.map((i) => ({
        id: i.Item_Id,
        code: i.Item_Code,
        name: i.Item_Name,
        qty: i.Item_Qty,
        unit: i.Item_Unit,
        price: i.Item_Price_Unit,
        discount: i.Discount_Value || 0,
        total: i.Item_Total,
        img: i.Item_Img || null,
      }))
    );

    // โหลดจุดย่อยที่ใช้ไปแล้ว (สัญญาอื่น) เพื่อแสดง "ใช้แล้ว" และปิดการเลือกซ้ำ
    if (h.Ref_DocNo && contractId) {
      try {
        const usedRes = await api.get(`/get-quote-used-sub-locations?quote_no=${encodeURIComponent(h.Ref_DocNo)}&exclude_contract_id=${contractId}`);
        if (usedRes.data?.status === "OK" && Array.isArray(usedRes.data.usedSubLocationIds)) {
          setHeader((prev) => ({ ...prev, Used_SubLocation_Ids: usedRes.data.usedSubLocationIds }));
        }
      } catch (_) {}
    }

    setServiceDetail({
      ExtraWorker: h.Sign_Name_More || "",
      ServiceNote: h.Service_Remark || "",
    });

    setPayments(res.data.payments || []);
    
    /**
     * ============================================
     * แปลงข้อมูล works จาก Backend Format → Frontend Format
     * ============================================
     * Backend Format (ที่ได้รับจาก API):
     * {
     *   Round_Id: 1, Round_No: 1, Round_Date: "2025-01-15",
     *   Services: [{ Service_Id: 1, Service_Name: "ปลวก", Steps: [...] }]
     * }
     * 
     * Frontend Format (ที่ใช้ใน component):
     * {
     *   id: "uuid", no: 1, date: "2025-01-15",
     *   services: [{ id: "uuid", name: "ปลวก", steps: [...] }]
     * }
     * 
     * ดูรายละเอียดเพิ่มเติมใน: CONTRACT_WORKS_API_SPEC.md
     * ============================================
     */
    const backendWorks = res.data.works || [];
    
    const formattedWorks = backendWorks.map((round) => ({
      id: round.id || `round-${round.Round_No || Date.now()}`,
      no: round.Round_No || round.no || 1,
      date: round.Round_Date || round.date || "",
      services: (round.Services || round.services || []).map((service) => ({
        id: service.id || `service-${Date.now()}-${Math.random()}`,
        name: service.Service_Name || service.name || "",
        serviceRefId: service.Service_Id ?? service.serviceRefId ?? null,
        serviceType: service.Service_Type || service.serviceType || null,
        serviceStatus: service.Service_Status ?? service.serviceStatus ?? 0,
        steps: (service.Steps || service.steps || []).map((step) => ({
          id: step.id || `step-${Date.now()}-${Math.random()}`,
          title: step.Step_Title || step.title || "",
          qty: step.Step_Qty !== undefined ? step.Step_Qty : (step.qty || ""),
          unit: step.Step_Unit || step.unit || "",
          remark: step.Step_Remark || step.remark || "",
          checked: step.Step_Checked === 1 || step.checked === true || step.checked === 1,
          refStepId: step.Ref_Step_Id || step.refStepId || null,
          itemId: step.Item_Id ?? step.itemId ?? null,
          itemName: step.Item_Name ?? step.itemName ?? "",
          itemCode: step.Item_Code ?? step.itemCode ?? "",
          itemGroup: step.Item_Group ?? step.itemGroup ?? null,
          isCollect: step.Step_IsCollect === 1 || step.isCollect === true || step.isCollect === 1,
        })),
      })),
    }));
    setWorks(formattedWorks);
  };

  const loadQuoteToContract = async (quoteId, arIdURL) => {
    const res = await api.get(`/get-quote/${quoteId}`);
    if (res.data.status !== "OK") throw new Error(res.data.message);

    const q = res.data.header;
    const d = res.data.details || [];

    const totalFromDetails = d.reduce((s, i) => s + (i.Item_Total || 0), 0);
    const discount = Number(q.Discount_Value || 0);
    // เมื่อ IsVatIn แปลงเป็น base (ใบเสนอราคาอาจเก็บ gross)
    const totalPrice = q.IsVatIn === 1 ? totalFromDetails / 1.07 : totalFromDetails;
    const net = totalPrice - discount;
    let vat = 0;
    let grand = net;
    if (q.IsVatIn === 1) {
      grand = net * 1.07;
      vat = grand - net;
    } else if (q.IsVat === 1) {
      vat = net * 0.07;
      grand = net + vat;
    }

    setHeader((prev) => ({
      ...prev,
      AR_Id: arIdURL ? Number(arIdURL) : q.AR_Id,
      AR_Name: q.AR_Name,
      AR_ContactName: q.AR_ContactName || "",
      DisplayName: q.DisplayName || "",
      AR_Tel: q.AR_Tel || "",
      AR_TaxNo: q.AR_TaxNo || "",
      Quote_Id: q.Quote_Id,
      Quote_No: q.Quote_No,
      ServiceAddr_Id: q.ServiceAddr_Id,
      ServiceAddr_Name: q.ServiceAddr_Name,
      Total_Price: totalPrice,
      Discount: discount,
      NetPrice: q.IsVatIn === 1 ? grand : net,
      VatAmount: vat,
      GrandTotal: grand,
      IsVat: q.IsVat,
      IsVatIn: q.IsVatIn ?? 0,
      Service_Name: q.Service_Name,
      Service_Time: q.Service_Time || 1,
      Service_Unit: q.Service_Unit ?? null,
      Contract_Period: q.Contract_Period || null,
      Contract_Unit: q.Contract_Unit || null,
    }));

    setServices(
      d.map((i) => ({
        id: i.Item_Id,
        code: i.Item_Code,
        name: i.Item_Name,
        qty: i.Item_Qty,
        unit: i.Item_Unit,
        price: i.Item_Price_Unit,
        discount: i.Discount_Value || 0,
        total: i.Item_Total,
        img: i.Item_Img || null,
      }))
    );
  };

  const loadQuoteHeaderOnly = async (quoteId, arIdURL) => {
    const res = await api.get(`/get-quote/${quoteId}`);
    if (res.data.status !== "OK") throw new Error(res.data.message);

    const q = res.data.header;

    setHeader(prev => ({
      ...prev,
      AR_Id: arIdURL ? Number(arIdURL) : q.AR_Id,
      AR_Name: q.AR_Name,
      AR_ContactName: q.AR_ContactName || "",
      DisplayName: q.DisplayName || "",
      AR_Tel: q.AR_Tel || "",
      AR_TaxNo: q.AR_TaxNo || "",

      Quote_Id: q.Doc_Id,
      Quote_No: q.Doc_No,

      Doc_Detail: q.Doc_Detail || "",
      Doc_Remark: q.Doc_Remark || "",

      ServiceAddr_Id: q.ServiceAddr_Id,
      ServiceAddr_Name: q.ServiceAddr_Name,

      IsVat: q.IsVat,
      IsVatIn: q.IsVatIn ?? 0,
      Total_Price: 0,
      Discount: 0,
      NetPrice: 0,
      VatAmount: 0,
      GrandTotal: 0,
      Service_Time: q.Service_Time || 1,
      Service_Unit: q.Service_Unit ?? null,
      Contract_Period: q.Contract_Period || null,
      Contract_Unit: q.Contract_Unit || null,
    }));

    setServices([]);
  };

  


  // ตรวจสอบว่าข้อมูลบริการครบหรือไม่ (ไม่รวม remark)
  const validateWorksComplete = (worksData) => {
    if (!worksData || worksData.length === 0) return false;

    for (const round of worksData) {
      if (!round.services || round.services.length === 0) return false;

      for (const service of round.services) {
        if (!service.steps || service.steps.length === 0) return false;

        for (const step of service.steps) {
          // ตรวจสอบ title, qty, unit (ไม่ตรวจ remark)
          if (!step.title || step.title.trim() === "") return false;
          if (!step.qty || step.qty.toString().trim() === "") return false;
          if (!step.unit || step.unit.trim() === "") return false;
        }
      }
    }

    return true;
  };

  // ตรวจสอบว่าสถานที่บริการมีการปักหมุดแล้วหรือยัง
  const checkServiceLocationPinned = async () => {
    if (!header.ServiceAddr_Id) return null; // ไม่มีสถานที่บริการที่เลือก
    
    try {
      const res = await api.get(`/get-tb-set-service-addr-by-id/${header.ServiceAddr_Id}`);
      if (res.data.status === "OK" && res.data.result?.recordset?.[0]) {
        const location = res.data.result.recordset[0];
        const hasLatitude = location.Latitude && location.Latitude.trim() !== "";
        const hasLongitude = location.Longitude && location.Longitude.trim() !== "";
        return hasLatitude && hasLongitude;
      }
      return false;
    } catch (err) {
      console.error("Error checking service location:", err);
      return false;
    }
  };

  const handleSave = async () => {
    try {
      if (!header.AR_Id)
        return Swal.fire("กรุณาเลือกลูกค้าก่อนบันทึก", "", "warning");
      if (services.length === 0)
        return Swal.fire("กรุณาเพิ่มบริการก่อนบันทึก", "", "warning");

      // ตรวจสอบ step ที่เลือกสินค้าแล้วแต่ปริมาณว่าง
      for (const round of works) {
        for (const srv of round.services || []) {
          for (const step of srv.steps || []) {
            if (step.itemId && (step.qty === "" || step.qty === null || step.qty === undefined || Number(step.qty) <= 0)) {
              return Swal.fire("กรุณากรอกปริมาณ", `ขั้นตอน "${step.title || "-"}" มีการเลือกสินค้าแต่ยังไม่ได้กรอกปริมาณ`, "warning");
            }
          }
        }
      }

      // ตรวจสอบว่ามีการปักหมุดสถานที่บริการแล้วหรือยัง และแสดง alert ยืนยันทุกครั้ง
      let isPinned = false;
      if (header.ServiceAddr_Id) {
        isPinned = await checkServiceLocationPinned();
      }

      // แสดง alert ยืนยันทุกครั้งก่อนบันทึก
      let warningMessages = [];
      
      if (header.ServiceAddr_Id) {
        if (isPinned) {
          warningMessages.push("⚠️ กรุณาตรวจเช็คสถานที่ปักหมุดทำบริการ");
        }
      }
      
      // แสดงข้อความเตือนเรื่องแก้ไขไม่ได้ทุกครั้ง
      warningMessages.push("⚠️ คำเตือน: หากบันทึกแล้ว จะไม่สามารถเปลี่ยนแปลงสัญญา จ๊อบงาน แฟ้มงาน ได้อีก");

      const confirm = await Swal.fire({
        title: "ยืนยันการบันทึกสัญญา",
        html: `
          <div style="text-align: left;">
            ${warningMessages.map(msg => `<p style="color: #d32f2f; font-weight: bold; margin-bottom: 10px;">${msg}</p>`).join('')}
            <p style="margin-top: 15px;">ต้องการบันทึกสัญญาหรือไม่?</p>
          </div>
        `,
        icon: "warning",
        showCancelButton: true,
        showDenyButton: !!header.ServiceAddr_Id, // แสดงปุ่มตรวจสอบเมื่อมีสถานที่บริการ (ทั้งสร้างและแก้ไข)
        confirmButtonText: "ยืนยันบันทึก",
        denyButtonText: "ตรวจสอบ",
        cancelButtonText: "ยกเลิก",
        reverseButtons: true,
        confirmButtonColor: "#d32f2f",
        denyButtonColor: "#1976d2",
      });

      if (confirm.isDenied) {
        setLocationModalOpen(true);
        return;
      }
      if (!confirm.isConfirmed) {
        return;
      }

      const total = Number(header.Total_Price) || 0;
      const discount = Number(header.Discount) || 0;
      const net = total - discount;
      let vat = 0;
      let grand = net;
      if (header.IsVatIn === 1) {
        grand = net * 1.07; // Total_Price = base
        vat = grand - net;
      } else if (header.IsVat === 1) {
        vat = net * 0.07;
        grand = net + vat;
      }

      const emp_id = Number(localStorage.getItem("emp_id")) || 1;
      const emp_name = localStorage.getItem("name") || "Admin";

      let docNo = header.Doc_No;

      if (!contractId) {
        const ctNo = await getSpRunNumber("CT");
        docNo = ctNo;

        setHeader((prev) => ({
          ...prev,
          Doc_No: ctNo,
        }));
      }

      /**
       * ============================================
       * แปลงข้อมูล works จาก Frontend Format → Backend Format
       * ============================================
       * ⚠️ สำคัญ: works จะถูกส่งไป backend เฉพาะเมื่อ Doc_Status = 4 (สัญญาเซ็นแล้ว) เท่านั้น
       * 
       * Frontend Format (ที่ใช้ใน component):
       * {
       *   id: "uuid", no: 1, date: "2025-01-15",
       *   services: [{ id: "uuid", name: "ปลวก", steps: [...] }]
       * }
       * 
       * Backend Format (ที่ส่งไป API):
       * {
       *   Round_No: 1, Round_Date: "2025-01-15",
       *   Services: [{ Service_Name: "ปลวก", Steps: [...] }]
       * }
       * 
       * ดูรายละเอียดเพิ่มเติมใน: CONTRACT_WORKS_API_SPEC.md
       * ============================================
       */
      const currentDocStatus = header.Doc_Status || 1;
      const shouldSaveWorks = Number(currentDocStatus) === 4; // เฉพาะเมื่อสัญญาเซ็นแล้ว
      
      const formattedWorks = shouldSaveWorks 
        ? (works || []).map((round, roundIdx) => ({
            Round_No: round.no || roundIdx + 1,
            Round_Date: round.date || null,
            Services: (round.services || []).map((service) => ({
              Service_Id: service.serviceRefId || null,
              Service_Name: service.name || "",
              Service_Type: service.serviceType || null,
              Service_Status: service.serviceStatus ?? 0,
              Steps: (service.steps || []).map((step) => ({
                Step_Title: step.title || "",
                Step_Qty: step.qty || "",
                Step_Unit: step.unit || "",
                Step_Remark: step.remark || "",
                Step_Checked: step.checked ? 1 : 0,
                Step_IsCollect: step.isCollect ? 1 : 0,
                Ref_Step_Id: step.refStepId || null,
                Item_Id: step.itemId || null,
                Item_Code: step.itemCode || null,
                Item_Name: step.itemName || null,
                Item_Group: step.itemGroup || null,
              })),
            })),
          }))
        : []; // ไม่ส่ง works ถ้ายังไม่ได้เซ็นสัญญา

      const payload = {
        header: {
          Comp_Id: header.Comp_Id,
          Doc_No: docNo,
          Doc_Date: header.Contract_Date
            ? new Date(header.Contract_Date)
            : new Date(),
          Ct_Start_Date: header.Start_Date
            ? new Date(header.Start_Date)
            : null,
          Ct_End_Date: header.End_Date ? new Date(header.End_Date) : null,
          AR_Id: header.AR_Id,
          AR_TaxNo: header.AR_TaxNo || "",
          DisplayName: header.DisplayName || "",
          AR_Tel: header.AR_Tel || "",
          AR_ContactName: header.AR_ContactName || "",
          ServiceAddr_Id: header.ServiceAddr_Id,
          ServiceAddr_Name: header.ServiceAddr_Name || "",
          ServiceAddr_FullText: header.ServiceAddr_FullText || "",
          Service_ContactName: header.Service_ContactName || "",
          Service_ContactTel: header.Service_ContactTel || "",
          Contract_ServiceAddr_Id: header.Contract_ServiceAddr_Id || null,
          Contract_ServiceAddr_Name: header.Contract_ServiceAddr_Name || "",
          Contract_ServiceAddr_FullText:
            header.Contract_ServiceAddr_FullText || "",
          Contract_ContactName: header.Contract_ContactName || "",
          Contract_ContactTel: header.Contract_ContactTel || "",
          Sale_Emp_Id: header.Sale_Emp_Id,
          Ref_DocID: header.Quote_Id || null,
          Ref_DocNo: header.Quote_No || null,
          Doc_Status: header.Doc_Status || 1,
          Doc_Detail: header.Doc_Detail || "",
          Doc_Remark: header.Doc_Remark || "",
          Contract_Lang: header.Language || "TH",
          Total_Price: total,
          Discount_Value: discount,
          IsVat: header.IsVat || 0,
          IsVatIn: header.IsVatIn || 0,
          NetTotal_Price: grand,
          Sign_Name_More: serviceDetail.ExtraWorker || "",
          Service_Remark: serviceDetail.ServiceNote || "",
          Created_By_Id: emp_id,
          Created_By_Name: emp_name,
          Update_By_Id: emp_id,
          Update_By_Name: emp_name,
          Sign_Password:
            header.Sign_Password ||
            Math.floor(100000 + Math.random() * 900000).toString(),
          Service_Name: header.Service_Name || "",
          Selected_SubLocation_Ids: header.Selected_SubLocation_Ids || [],
        },
        services,
        serviceDetail,
        payments: (payments || [])
          .filter((p) => {
            // กรองเฉพาะ object ที่มีข้อมูล date และ amount
            if (!p || typeof p !== 'object') return false;
            const hasDate = 'date' in p || 'CT_Due_Date' in p;
            const hasAmount = 'amount' in p || 'CT_Due_Amount' in p;
            return hasDate && hasAmount;
          })
          .map((p) => {
            // แปลงเป็น format ที่ backend ต้องการ
            const date = p.date || p.CT_Due_Date;
            const amount = Number(p.amount || p.CT_Due_Amount || 0);
            const invoice_no = p.invoice_no || p.Invoice_No || "";
            
            // ตรวจสอบว่าข้อมูลถูกต้อง
            if (!date || amount <= 0) return null;
            
            return {
              date: date,
              amount: amount,
              // invoice_no จะถูก generate ที่ backend อัตโนมัติ
            };
          })
          .filter((p) => p !== null), // กรอง null ออก
        works: formattedWorks,
      };

      console.log("[ContractSave] header.Language =", header.Language, "| payload.header.Contract_Lang =", payload.header.Contract_Lang);
      const res = contractId
        ? await api.put(`/update-contract/${contractId}`, payload)
        : await api.post("/create-contract", payload);

      // ตรวจสอบว่าข้อมูลบริการครบหรือไม่ (เฉพาะเมื่อ Doc_Status === 4)
      const isSigned = Number(currentDocStatus) === 4;
      const worksComplete = isSigned && validateWorksComplete(works);

      if (worksComplete) {
        // แสดง alert และนำไปหน้าแฟมข้อมูล
        const savedContractId = res.data?.Doc_Id || contractId || res.data?.result?.Doc_Id;
        await Swal.fire({
          title: "ทำการ Assign Job เรียบร้อย",
          text: "ระบบจะนำข้อมูลไปที่หน้าแฟมข้อมูลต่อไป",
          icon: "success",
          confirmButtonText: "ตกลง",
        });

        // นำไปหน้าแฟมข้อมูล
        if (savedContractId) {
          navigate(`/jobfile/${savedContractId}`);
        } else {
          navigate(`/contractlist/${header.AR_Id}`);
        }
      } else {
        // บันทึกเฉยๆ ไม่แสดง alert
        navigate(`/contractlist/${header.AR_Id}`);
      }
    } catch (err) {
      console.error("Error save contract:", err);
      Swal.fire("เกิดข้อผิดพลาด", err.message, "error");
    }
  };

  const handleCancelContract = async () => {
    if (!contractId)
      return Swal.fire("ไม่พบเลขที่สัญญา", "", "warning");

    const confirm = await Swal.fire({
      title: "ต้องการยกเลิกสัญญานี้หรือไม่?",
      text: "การยกเลิกจะไม่สามารถย้อนกลับได้",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
    });

    if (!confirm.isConfirmed) return;

    try {
      const emp_id = Number(localStorage.getItem("emp_id")) || 0;
      const emp_name = localStorage.getItem("name") || "";

      await api.put(`/cancel-contract/${contractId}`, {
        Cancel_By_Id: emp_id,
        Cancel_By_Name: emp_name,
      });

      navigate(`/contractlist/${header.AR_Id}`);
    } catch (err) {
      console.error("Cancel Contract Error:", err);
      Swal.fire("เกิดข้อผิดพลาด", err.message, "error");
    }
  };

  const handleSelectService = (service) => {
    const exists = services.some((s) => s.id === service.Item_Id);
    if (exists) {
      Swal.fire("ข้อมูลซ้ำ!", "บริการนี้ถูกเพิ่มแล้ว", "warning");
      setModalOpen(false);
      return;
    }

    const total = Number(service.Item_Qty) * Number(service.Item_Price_Unit);
    const discount = Number(service.Item_Discount) || 0;
    const net = total - discount;
    const isVat = service.IsVat === 1 ? 1 : 0;
    const vat = isVat ? net * 0.07 : 0;
    const grand = net + vat;

    const newService = {
      id: service.Item_Id,
      code: service.Item_Code,
      name: service.Item_Name,
      qty: service.Item_Qty,
      unit: service.Item_Unit,
      price: service.Item_Price_Unit,
      discount: service.Item_Discount,
      total: grand,
      img: service.Item_Img || null,
    };

    setServices((prev) => [...prev, newService]);
    setModalOpen(false);
  };

  // รวมราคา = รวมจากบริการที่หักส่วนลดแล้ว
  // หากมีจุดย่อย: รวมจากจุดย่อยที่ติ๊กถูกเท่านั้น (หักส่วนลดในแต่ละจุดแล้ว)
  // เมื่อ IsVatIn เก็บ base (หาร 1.07) ใน Total_Price
  useEffect(() => {
    const subs = header.Quote_SubLocations || [];
    const selectedIds = header.Selected_SubLocation_Ids || [];

    let netTotal = 0;
    if (subs.length > 0 && selectedIds.length > 0) {
      // มีจุดย่อย → รวมจากจุดที่ติ๊กถูกเท่านั้น (หักส่วนลดในแต่ละจุดแล้ว)
      netTotal = subs
        .filter((sub) => selectedIds.includes(sub.Sub_Id))
        .reduce((sum, sub) => {
          const qty = Number(sub.Qty) || 0;
          const price = Number(sub.Price_Unit) || 0;
          const discount = Number(sub.Discount) || 0;
          return sum + (qty * price - discount);
        }, 0);
    } else {
      // ไม่มีจุดย่อย → รวมจากบริการ (services)
      const totalPrice = services.reduce((sum, s) => {
        const serviceTotal = (Number(s.qty) || 0) * (Number(s.price) || 0);
        return sum + serviceTotal;
      }, 0);
      const totalDiscount = services.reduce((sum, s) => sum + (Number(s.discount) || 0), 0);
      netTotal = totalPrice - totalDiscount;
    }

    setHeader((prev) => {
      // IsVatIn เท่านั้นที่เก็บ base (หาร 1.07) — IsVat เก็บ รวมราคา โดยตรง แล้ว +7%
      const total = prev.IsVatIn === 1 ? netTotal / 1.07 : netTotal;
      return {
        ...prev,
        Total_Price: total,
        Discount: 0,
      };
    });
  }, [services, header.Quote_SubLocations, header.Selected_SubLocation_Ids, header.IsVat, header.IsVatIn]);

  const role = localStorage.getItem("role");
  const isAdmin = role === "Admin";

  const contractListLink = location.state?.contractListLink
    || (header.AR_Id ? `/contractlist/${header.AR_Id}` : "/contractlist");
  const contractListLabel = location.state?.contractListLabel || "สัญญา";
  const parentBackLink = location.state?.parentBackLink || null;
  const parentBackLabel = location.state?.parentBackLabel || "ใบเสนอราคา";
  const fromTempEquipment = location.state?.fromTempEquipment === true;
  const tempEquipmentBackLink = location.state?.tempEquipmentBackLink || location.state?.backLink || "/temp-equipment";
  const tempEquipmentBackLabel = location.state?.tempEquipmentBackLabel || location.state?.backLabel || "รายการอุปกรณ์ชั่วคราว";
  const fromStockHistory = location.state?.fromStockHistory === true;
  const stockOverviewLink = location.state?.stockOverviewLink || "/stock/stock-overview";
  const stockOverviewLabel = location.state?.stockOverviewLabel || "ดูสต๊อกสินค้า";
  const stockHistoryBackLink = location.state?.stockHistoryBackLink || location.state?.backLink || "/stock/stock-detail";
  const stockHistoryBackLabel = location.state?.stockHistoryBackLabel || "ประวัติการเคลื่อนไหวสินค้า";
  const fromJobFile = location.state?.fromJobFile === true;
  const jobFileBackLink = location.state?.jobFileBackLink || "/jobfile";
  const fromInvoiceRecord = location.state?.fromInvoiceRecord === true;
  const invoiceRecordBackLink = location.state?.invoiceRecordBackLink || "/invoice-record";
  const invoiceRecordBackLabel = location.state?.invoiceRecordBackLabel || "บันทึกการชำระเงิน";
  const fromPage = location.state?.from || null;
  const fromJobId = location.state?.jobId || null;
  const fromJobManagement = location.state?.fromJobManagement === true;
  const jobManagementBackLink = location.state?.jobManagementBackLink || "/job-management";
  const jobManagementBackLabel = location.state?.jobManagementBackLabel || "จัดการจ๊อบงาน";
  const contractNoSuffix = header.Doc_No ? ` ${header.Doc_No}` : "";
  const pageTitle = isViewMode
    ? `ดูข้อมูลสัญญา${contractNoSuffix}`
    : contractId
      ? `แก้ไขสัญญา${contractNoSuffix}`
      : "สร้างสัญญาใหม่";
  const topbarBreadcrumbs = fromPage
    ? [
        { label: "ปฏิทินงานช่าง", link: "/job/calendar" },
        ...(fromJobId ? [{ label: `งาน ${fromJobId}`, link: fromPage.path }] : []),
        { label: fromPage.label, link: fromPage.path },
        { label: pageTitle },
      ]
    : fromInvoiceRecord
    ? [
        { label: "งานบัญชี", link: "/invoice-record" },
        { label: invoiceRecordBackLabel, link: invoiceRecordBackLink },
        { label: pageTitle },
      ]
    : fromStockHistory
    ? [
        { label: "สต๊อกสินค้า", link: "/stock/stock-overview" },
        { label: stockOverviewLabel, link: stockOverviewLink },
        { label: stockHistoryBackLabel, link: stockHistoryBackLink },
        { label: pageTitle },
      ]
    : fromTempEquipment
    ? [
        { label: "งานช่าง", link: "/job-management" },
        { label: tempEquipmentBackLabel, link: tempEquipmentBackLink },
        { label: pageTitle },
      ]
    : fromJobManagement
    ? [
        { label: "งานช่าง", link: "/job-management" },
        { label: jobManagementBackLabel, link: jobManagementBackLink, state: { activeTab: "list" } },
        { label: pageTitle },
      ]
    : fromJobFile
    ? [
        { label: "งานช่าง", link: "/job-management" },
        { label: "แฟ้มงาน", link: jobFileBackLink },
        { label: pageTitle },
      ]
    : [
        { label: "ลูกค้า", link: "/sale" },
        { label: "รายการลูกค้า", link: "/sale" },
        ...(parentBackLink ? [{ label: parentBackLabel, link: parentBackLink }] : []),
        { label: contractListLabel, link: contractListLink },
        { label: pageTitle },
      ];
  const backToContractList = () =>
    navigate(contractListLink, {
      state: {
        backLink: parentBackLink,
        backLabel: parentBackLabel,
      },
    });


  return (
    <div className="flex text-14 h-screen overflow-hidden">
      <Sidebar />
      <main
        className="flex-1 h-screen bg-gray-50 flex flex-col"
        style={{
          height: "calc(100vh - 56px)",
          overflowX: "hidden",
          overflowY: "hidden",
          marginLeft: "var(--sidebar-width)",
          marginTop: "56px",
        }}
      >
        <div className="flex-shrink-0 pl-2 pr-6 pt-0.5">
          <Topbar breadcrumbs={topbarBreadcrumbs} />
        </div>

        <div className="flex-1 min-h-0 overflow-auto pl-2 pr-6 pb-6 pt-0.5 space-y-6">

          {/* ตรวจสอบว่าเป็น read-only mode หรือไม่ (Status === 4 หรือ view mode) */}
          {(() => {
            const isReadOnly = Number(header.Doc_Status) === 4 || isViewMode;
            
            return (
              <>
                <ContractHeader
                  header={header}
                  setHeader={setHeader}
                  contractId={contractId}
                  quoteLinkState={{
                    backLink: `${location.pathname}${location.search || ""}`,
                    backLabel: `แก้ไขสัญญา${header.Doc_No ? ` ${header.Doc_No}` : ""}`,
                    contractListLink,
                    contractListLabel,
                  }}
                  setReload={setReload}
                  serviceLocations={serviceLocations}
                  contractLocation={contractLocation}
                  quoteIdFromURL={quoteIdFromURL}
                  isReadOnly={isReadOnly}
                  title={pageTitle}
                  showPrintButton={!!contractId && Number(header.Doc_Status) !== 5}
                  onPrint={() => {
                    if (!header.Doc_Id)
                      return Swal.fire(
                        "ไม่พบเลขที่สัญญา",
                        "ไม่สามารถพิมพ์รายงานได้",
                        "warning"
                      );
                    getReport("contract", header.Doc_Id);
                  }}
                  onEditContractLocation={() => setContractLocationModalOpen(true)}
                  onEditServiceLocation={() => setLocationModalOpen(true)}
                  onQuoteChange={() => {
                    // ล้างค่าบริการและข้อมูลที่เกี่ยวข้องเมื่อเลือกใบเสนอราคาใหม่
                    setServices([]);
                    setPayments([]);
                    setWorks([]);
                    setServiceDetail({
                      ExtraWorker: "",
                      ServiceNote: "",
                    });
                  }}
                />
                <ContractServiceTable
                  services={services}
                  setServices={setServices}
                  onAdd={() => setModalOpen(true)}
                  isReadOnly={isReadOnly}
                  hasSubLocations={(header.Quote_SubLocations?.length || 0) > 0}
                />
                <ContractServiceDetail
                  serviceDetail={serviceDetail}
                  setServiceDetail={setServiceDetail}
                  isReadOnly={isReadOnly}
                />
                <ContractPaymentTable
                  payments={payments}
                  setPayments={setPayments}
                  grandTotal={header.GrandTotal}
                  onValidationChange={setIsPaymentValid}
                  isReadOnly={isReadOnly}
                  startDate={header.Start_Date}
                  endDate={header.End_Date}
                />

                {Number(header.Doc_Status) === 4 && (
                  <ContractServiceWorkTable
                    works={works}
                    setWorks={setWorks}
                    startDate={header.Start_Date}
                    endDate={header.End_Date}
                    serviceTime={header.Service_Time}
                    serviceUnit={header.Service_Unit}
                    contractPeriod={header.Contract_Period}
                    contractUnit={header.Contract_Unit}
                    contractServices={services}
                    hasSubLocations={(header.Quote_SubLocations?.length || 0) > 0}
                    isReadOnly={false}
                  />
                )}

                {Number(header.Doc_Status) !== 5 && (
                  <ContractActions
                    onSave={handleSave}
                    onCancel={backToContractList}
                    onCancelContract={handleCancelContract}
                    isEdit={!!contractId}
                    docStatus={header.Doc_Status}
                    isPaymentValid={isPaymentValid}
                    isReadOnly={false}
                  />
                )}
              </>
            );
          })()}
        </div>
      </main>

      {modalOpen && (
        <ServiceModal
          isOpen={modalOpen}
          docId={quoteIdFromURL || header?.Quote_Id}
          onClose={() => setModalOpen(false)}
          onSelect={handleSelectService}
        />
      )}

      {locationModalOpen && header.ServiceAddr_Id && header.AR_Id && (
        <LocationModal
          isOpen={locationModalOpen}
          onClose={() => setLocationModalOpen(false)}
          ServiceAddr_Id={header.ServiceAddr_Id}
          AR_Id={header.AR_Id}
          defaultServicePurpose="6"
          fromCustomerPage={true}
          serviceTypes={serviceTypeMaster}
          onSave={async (formData) => {
            try {
              const dataToSave = {
                ...formData,
                AR_Id: header.AR_Id,
                ServicePurpose: "2",
                Comp_Id: formData.Comp_Id || 1,
              };
              let res;
              if (formData.ServiceAddr_Id) {
                res = await api.put(`/update-tb-set-service-addr/${formData.ServiceAddr_Id}`, dataToSave);
              } else {
                res = await api.post(`/create-tb-set-service-addr`, dataToSave);
              }
              if (res.data.status === "OK") {
                const savedId = formData.ServiceAddr_Id || res.data.ServiceAddr_Id;
                setPendingRefreshServiceAddr(savedId);
                setLocationModalOpen(false);
                return { status: "OK", ServiceAddr_Id: savedId };
              }
              return { status: "FAILED" };
            } catch (err) {
              console.error("Save location error:", err);
              Swal.fire("ผิดพลาด", "ไม่สามารถบันทึกข้อมูลได้", "error");
              return { status: "FAILED" };
            }
          }}
        />
      )}

      {contractLocationModalOpen && header.Contract_ServiceAddr_Id && header.AR_Id && (
        <LocationModal
          isOpen={contractLocationModalOpen}
          onClose={() => setContractLocationModalOpen(false)}
          ServiceAddr_Id={header.Contract_ServiceAddr_Id}
          AR_Id={header.AR_Id}
          defaultServicePurpose="7"
          fromCustomerPage={true}
          serviceTypes={serviceTypeMaster}
          onSave={async (formData) => {
            try {
              const dataToSave = {
                ...formData,
                AR_Id: header.AR_Id,
                ServicePurpose: "3",
                Comp_Id: formData.Comp_Id || 1,
              };
              let res;
              if (formData.ServiceAddr_Id) {
                res = await api.put(`/update-tb-set-service-addr/${formData.ServiceAddr_Id}`, dataToSave);
              } else {
                res = await api.post(`/create-tb-set-service-addr`, dataToSave);
              }
              if (res.data.status === "OK") {
                const savedId = formData.ServiceAddr_Id || res.data.ServiceAddr_Id;
                setPendingRefreshContractAddr(savedId);
                setContractLocationModalOpen(false);
                return { status: "OK", ServiceAddr_Id: savedId };
              }
              return { status: "FAILED" };
            } catch (err) {
              console.error("Save contract location error:", err);
              Swal.fire("ผิดพลาด", "ไม่สามารถบันทึกข้อมูลได้", "error");
              return { status: "FAILED" };
            }
          }}
        />
      )}
    </div>
  );
}
